import { Button, useLocalization } from '@magicdoorinc/ui';
import { createEffect, untrack, createMemo, Show } from 'solid-js';
import IconPlus from '~/assets/images/common/plus.svg?component-solid';
import BankAccountChoose from '~/components/company-type-confirm/BankAccountChoose';
import { CompanyTypeConfirmStep, useBankAccountsList, useCompanyTypeConfirm } from '~/contexts/local';
import type { Component } from 'solid-js';

const SelectCompanyBankAccountsContent: Component = () => {
  const { t } = useLocalization();
  const { resolveSelfManagedStore, setResolveSelfManagedStore, unassignedBankAccounts } = useCompanyTypeConfirm();

  const { bankAccounts } = useBankAccountsList();

  const allAccountsRestricted = createMemo(() => {
    const accounts = bankAccounts();
    const unassigned = unassignedBankAccounts();
    if (!accounts || !unassigned || accounts.length === 0) return false;

    return accounts.every((acc) => {
      const restriction = unassigned.find((u) => u.id === acc.id);
      return restriction?.cannotBeCompanyAccount === true;
    });
  });

  const handlePropertyAccountChange = (accountIds: string[]) => {
    setResolveSelfManagedStore({
      propertyBankAccountIds: accountIds,
    });
  };

  const handleCompanyAccountChange = (accountIds: string[]) => {
    setResolveSelfManagedStore({
      companyBankAccountIds: accountIds,
    });
  };

  createEffect(() => {
    const _bankAccounts = bankAccounts();
    if (!_bankAccounts) return;
    untrack(() => {
      for (const bankAccount of _bankAccounts) {
        if (bankAccount.companyAccount) {
          setResolveSelfManagedStore({
            companyBankAccountIds: [...(resolveSelfManagedStore.companyBankAccountIds || []), bankAccount.id],
          });
        } else {
          setResolveSelfManagedStore({
            propertyBankAccountIds: [...(resolveSelfManagedStore.propertyBankAccountIds || []), bankAccount.id],
          });
        }
      }
    });
  });

  return (
    <div class="p-5">
      <div class="flex flex-col gap-5">
        <div class="flex flex-col gap-2">
          <h3 class="text-sm font-medium text-text-01">{t('Select accounts')}</h3>
          <p class="text-sm text-primary">
            {t(
              'Select all property accounts, that you will use to receive rents and pay property expenses, and your company operational accounts for company expenses.'
            )}
          </p>
        </div>
        <Show when={allAccountsRestricted()}>
          <div class="rounded-md bg-red-50 p-4">
            <p class="text-sm font-medium text-danger">
              {t('None of your existing accounts can be used as a company account. Please create a new bank account to continue.')}
            </p>
          </div>
        </Show>
        <BankAccountChoose
          propertyAccountIds={resolveSelfManagedStore.propertyBankAccountIds}
          companyAccountIds={resolveSelfManagedStore.companyBankAccountIds}
          onPropertyAccountChange={handlePropertyAccountChange}
          onCompanyAccountChange={handleCompanyAccountChange}
        />
      </div>
    </div>
  );
};

export const SelectCompanyBankAccountsFooter: Component = () => {
  const { t } = useLocalization();
  const { setStep, resolveSelfManagedStore, handleSelectBankAccounts } = useCompanyTypeConfirm();

  const handleCancel = () => {
    setStep(CompanyTypeConfirmStep.SelectCompanyType);
  };

  const hasNoCompanyAccounts = () => !resolveSelfManagedStore.companyBankAccountIds?.length;

  return (
    <div class="flex flex-col border-t border-separator bg-white px-5 py-4">
      <Show when={hasNoCompanyAccounts()}>
        <div class="mb-3 text-sm text-danger">{t('Please select at least one company account to continue.')}</div>
      </Show>
      <div class="flex items-center justify-between">
        <Button variant="outlined" onClick={handleCancel}>
          {t('Back')}
        </Button>
        <div class="flex items-center gap-2">
          <Button
            variant="outlined"
            onClick={() => setStep(CompanyTypeConfirmStep.CreateCompanyBankAccount)}
            class="flex items-center justify-center gap-1 border-primary px-5 py-2.5 text-primary">
            <IconPlus class="size-5" />
            <span>{t('New account')}</span>
          </Button>
          <Button onClick={handleSelectBankAccounts} disabled={hasNoCompanyAccounts()}>
            {t('Next')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SelectCompanyBankAccountsContent;
